import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { API_BASE_URL } from "../config";
import {
    FiShoppingBag,
    FiDollarSign,
    FiUsers,
    FiClock,
    FiTrendingUp,
    FiGift,
    FiUserPlus,
    FiRefreshCw,
    FiActivity,
    FiCreditCard,
    FiCalendar,
} from "react-icons/fi";
import { toast } from "react-toastify";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { format, subDays } from "date-fns";
import { useTheme } from "../contexts/ThemeContext";
import {
    Card,
    CardBody,
    StatCard,
    StatusBadge,
    Button,
    PageHeader,
    LoadingSkeleton,
    Section,
    EmptyState,
    timeAgo,
} from "./ui";

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

export default function Dashboard() {
    const { theme } = useTheme();
    const [stats, setStats] = useState({
        totalToko: 0,
        totalPembayaran: 0,
        totalRevenue: 0,
        thisMonthRevenue: 0,
        pendingPembayaran: 0,
        activeReferrals: 0,
        pendingWithdrawals: 0,
    });
    const [recentActivity, setRecentActivity] = useState({
        pembayaran: [],
        toko: [],
        penarikan: [],
        activeStores: [],
    });
    const [chartData, setChartData] = useState({
        revenue: [],
        payments: [],
        statusDistribution: {},
    });
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [registrationOpen, setRegistrationOpen] = useState(true);
    const [togglingRegistration, setTogglingRegistration] = useState(false);

    useEffect(() => {
        fetchDashboardData();
        fetchRegistrationStatus();

        const tokoSub = supabase
            .channel('dashboard-toko')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'toko' }, fetchDashboardData)
            .subscribe();

        const pembayaranSub = supabase
            .channel('dashboard-pembayaran')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'pembayaran' }, fetchDashboardData)
            .subscribe();

        return () => {
            supabase.removeChannel(tokoSub);
            supabase.removeChannel(pembayaranSub);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchRegistrationStatus = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/admin/pengaturan/pendaftaran_buka`);
            const data = await res.json();
            setRegistrationOpen(data.value !== "false");
        } catch (err) {
            console.error("Error fetching registration status:", err);
        }
    };

    const toggleRegistration = async () => {
        setTogglingRegistration(true);
        try {
            const newValue = registrationOpen ? "false" : "true";
            const res = await fetch(`${API_BASE_URL}/api/admin/pengaturan`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ key: "pendaftaran_buka", value: newValue })
            });
            const data = await res.json();
            if (data.success) {
                setRegistrationOpen(!registrationOpen);
                toast.success(registrationOpen ? "Pendaftaran ditutup" : "Pendaftaran dibuka");
            } else {
                toast.error("Gagal mengubah status pendaftaran");
            }
        } catch (err) {
            toast.error("Error: " + err.message);
        } finally {
            setTogglingRegistration(false);
        }
    };

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const [tokoRes, pembayaranRes, referalRes, penarikanRes] = await Promise.all([
                supabase.from('toko').select('*'),
                supabase.from('pembayaran').select('*').order('created_at', { ascending: false }),
                supabase.from('referal').select('*'),
                supabase.from('penarikan').select('*'),
            ]);

            const totalToko = tokoRes.data?.length || 0;
            const pembayaranBerhasil = pembayaranRes.data?.filter(p => p.keterangan === 'berhasil') || [];
            const totalRevenue = pembayaranBerhasil.reduce((sum, p) => sum + (parseInt(p.amount) || 0), 0);

            const currentMonth = new Date().getMonth();
            const currentYear = new Date().getFullYear();
            const thisMonthRevenue = pembayaranBerhasil
                .filter(p => {
                    const d = new Date(p.created_at);
                    return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
                })
                .reduce((sum, p) => sum + (parseInt(p.amount) || 0), 0);

            const activeStoresData = {};
            pembayaranBerhasil.forEach(p => {
                if (!activeStoresData[p.kode_toko]) {
                    const storeInfo = tokoRes.data?.find(t => t.kode_toko === p.kode_toko) || {};
                    activeStoresData[p.kode_toko] = {
                        kode_toko: p.kode_toko,
                        email: storeInfo.email || 'N/A',
                        totalAmount: 0,
                        paymentCount: 0,
                        latestPayment: p.created_at,
                        expiry_duration: p.expiry_duration
                    };
                }
                activeStoresData[p.kode_toko].totalAmount += (parseInt(p.amount) || 0);
                activeStoresData[p.kode_toko].paymentCount += 1;
                if (new Date(p.created_at) > new Date(activeStoresData[p.kode_toko].latestPayment)) {
                    activeStoresData[p.kode_toko].latestPayment = p.created_at;
                    activeStoresData[p.kode_toko].expiry_duration = p.expiry_duration;
                }
            });
            const activeStoresList = Object.values(activeStoresData).sort((a, b) => new Date(b.latestPayment) - new Date(a.latestPayment));

            const pendingPembayaran = pembayaranRes.data?.filter(p => p.keterangan === 'pending').length || 0;
            const activeReferrals = referalRes.data?.filter(r => r.status === 'disetujui').length || 0;
            const pendingWithdrawals = penarikanRes.data?.filter(w => w.status === 'pending').length || 0;

            setStats({
                totalToko,
                totalPembayaran: pembayaranBerhasil.length,
                totalRevenue,
                thisMonthRevenue,
                pendingPembayaran,
                activeReferrals,
                pendingWithdrawals,
            });

            setRecentActivity({
                pembayaran: pembayaranRes.data?.slice(0, 5) || [],
                toko: tokoRes.data?.slice(-5).reverse() || [],
                penarikan: penarikanRes.data?.slice(0, 5) || [],
                activeStores: activeStoresList,
            });

            const last7Days = [...Array(7)].map((_, i) => {
                const date = subDays(new Date(), 6 - i);
                return format(date, 'yyyy-MM-dd');
            });

            const revenueByDay = last7Days.map(date => {
                const dayPayments = pembayaranBerhasil.filter(p => {
                    const paymentDate = format(new Date(p.created_at), 'yyyy-MM-dd');
                    return paymentDate === date;
                });
                return dayPayments.reduce((sum, p) => sum + (parseInt(p.amount) || 0), 0);
            });

            const paymentsByDay = last7Days.map(date => {
                return pembayaranBerhasil.filter(p => {
                    const paymentDate = format(new Date(p.created_at), 'yyyy-MM-dd');
                    return paymentDate === date;
                }).length;
            });

            const statusCounts = pembayaranRes.data?.reduce((acc, p) => {
                const status = p.keterangan || 'unknown';
                acc[status] = (acc[status] || 0) + 1;
                return acc;
            }, {}) || {};

            setChartData({
                revenue: revenueByDay,
                payments: paymentsByDay,
                statusDistribution: statusCounts,
                labels: last7Days.map(d => format(new Date(d), 'dd MMM')),
            });

            setHasError(false);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setHasError(true);
        } finally {
            setLoading(false);
        }
    };

    const isDark = theme === 'dark';
    const gridColor = isDark ? 'rgba(148, 163, 184, 0.14)' : 'rgba(100, 116, 139, 0.12)';
    const tickColor = isDark ? '#94a3b8' : '#64748b';

    const revenueChartData = {
        labels: chartData.labels || [],
        datasets: [
            {
                label: 'Revenue (Rp)',
                data: chartData.revenue || [],
                borderColor: 'rgb(59, 130, 246)',
                backgroundColor: 'rgba(59, 130, 246, 0.12)',
                tension: 0.4,
                fill: true,
                pointRadius: 3,
                pointBackgroundColor: 'rgb(59, 130, 246)',
                pointBorderColor: isDark ? '#0f172a' : '#ffffff',
                pointBorderWidth: 2,
            },
        ],
    };

    const paymentsChartData = {
        labels: chartData.labels || [],
        datasets: [
            {
                label: 'Pembayaran',
                data: chartData.payments || [],
                backgroundColor: 'rgba(139, 92, 246, 0.75)',
                hoverBackgroundColor: 'rgba(139, 92, 246, 1)',
                borderRadius: 8,
                maxBarThickness: 36,
            },
        ],
    };

    const statusChartData = {
        labels: Object.keys(chartData.statusDistribution || {}),
        datasets: [
            {
                data: Object.values(chartData.statusDistribution || {}),
                backgroundColor: [
                    'rgba(34, 197, 94, 0.85)',
                    'rgba(251, 191, 36, 0.85)',
                    'rgba(239, 68, 68, 0.85)',
                    'rgba(59, 130, 246, 0.85)',
                ],
                borderColor: isDark ? '#0f172a' : '#ffffff',
                borderWidth: 3,
                hoverOffset: 4,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: isDark ? '#1e293b' : '#0f172a',
                padding: 12,
                cornerRadius: 10,
                titleFont: { size: 12, family: 'Inter' },
                bodyFont: { size: 12, family: 'Inter' },
                displayColors: false,
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: tickColor, font: { size: 11 } },
            },
            y: {
                beginAtZero: true,
                grid: { color: gridColor, drawBorder: false },
                ticks: { color: tickColor, font: { size: 11 } },
            },
        },
    };

    // Combined activity feed (payments + stores + withdrawals)
    const feed = [
        ...recentActivity.pembayaran.map((p) => ({
            id: `p-${p.id ?? p.kode_toko}-${p.created_at}`,
            kind: 'payment',
            title: `Pembayaran ${p.kode_toko}`,
            description: `Rp ${(parseInt(p.amount) || 0).toLocaleString('id-ID')}`,
            time: p.created_at,
            status: p.keterangan,
        })),
        ...recentActivity.toko.map((t) => ({
            id: `t-${t.user_id}`,
            kind: 'store',
            title: `Toko baru ${t.kode_toko}`,
            description: t.email,
            time: t.created_at,
        })),
        ...recentActivity.penarikan.map((w) => ({
            id: `w-${w.id ?? w.user_id}`,
            kind: 'withdrawal',
            title: `Penarikan ${w.user_id}`,
            description: `Rp ${(parseInt(w.jumlah) || 0).toLocaleString('id-ID')}`,
            time: w.created_at,
            status: w.status,
        })),
    ]
        .sort((a, b) => new Date(b.time) - new Date(a.time))
        .slice(0, 8);

    const feedStyles = {
        payment: { icon: FiDollarSign, cls: 'bg-success-50 text-success-600 dark:bg-success-500/10 dark:text-success-400' },
        store: { icon: FiShoppingBag, cls: 'bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400' },
        withdrawal: { icon: FiGift, cls: 'bg-warning-50 text-warning-600 dark:bg-warning-500/10 dark:text-warning-400' },
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="h-9 w-48 rounded-lg bg-slate-200 dark:bg-slate-800" />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {[...Array(8)].map((_, i) => (
                        <LoadingSkeleton key={i} type="card" />
                    ))}
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <LoadingSkeleton type="chart" className="lg:col-span-2" />
                    <LoadingSkeleton type="chart" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <PageHeader
                title="Dashboard"
                subtitle="Overview sistem XF Admin secara real-time"
                actions={
                    <div className="flex items-center gap-2">
                        <span className="hidden items-center gap-2 rounded-xl border border-slate-200/80 bg-white px-3.5 py-2 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400 sm:flex">
                            <FiCalendar className="h-4 w-4" />
                            {new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                        <Button variant="secondary" icon={FiRefreshCw} onClick={() => { fetchDashboardData(); fetchRegistrationStatus(); }}>
                            Refresh
                        </Button>
                    </div>
                }
            />

            {hasError && (
                <div className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:border-danger-500/20 dark:bg-danger-500/10 dark:text-danger-300">
                    Gagal memuat sebagian data dashboard. Coba refresh.
                </div>
            )}

            {/* Statistics */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard label="Total Toko" value={stats.totalToko} icon={FiShoppingBag} tone="primary" hint="Toko terdaftar" />
                <StatCard label="Total Pembayaran" value={stats.totalPembayaran} icon={FiCreditCard} tone="secondary" hint="Pembayaran berhasil" />
                <StatCard label="Total Revenue" value={`Rp ${stats.totalRevenue.toLocaleString('id-ID')}`} icon={FiDollarSign} tone="success" hint="Akumulasi pendapatan" />
                <StatCard label="Revenue Bulan Ini" value={`Rp ${stats.thisMonthRevenue.toLocaleString('id-ID')}`} icon={FiTrendingUp} tone="success" hint="Bulan berjalan" />
                <StatCard label="Pending Pembayaran" value={stats.pendingPembayaran} icon={FiClock} tone="warning" hint="Menunggu verifikasi" />
                <StatCard label="Active Referral" value={stats.activeReferrals} icon={FiUsers} tone="primary" hint="Referal disetujui" />
                <StatCard label="Pending Withdrawal" value={stats.pendingWithdrawals} icon={FiGift} tone="danger" hint="Menunggu persetujuan" />
                <StatCard
                    label="Status Sistem"
                    value={hasError ? 'Gangguan' : 'Online'}
                    icon={FiActivity}
                    tone={hasError ? 'danger' : 'success'}
                    hint={hasError ? 'Periksa koneksi data' : 'Data tersinkron'}
                />
            </div>

            {/* Registration control */}
            <Card className="p-4">
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-3">
                        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${registrationOpen ? 'bg-success-50 text-success-600 dark:bg-success-500/10 dark:text-success-400' : 'bg-danger-50 text-danger-600 dark:bg-danger-500/10 dark:text-danger-400'}`}>
                            <FiUserPlus className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Pendaftaran Toko</p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                {registrationOpen ? 'Pendaftaran toko baru sedang dibuka' : 'Pendaftaran toko baru sedang ditutup'}
                            </p>
                        </div>
                    </div>
                    <Button
                        variant={registrationOpen ? 'danger' : 'success'}
                        icon={FiUserPlus}
                        loading={togglingRegistration}
                        onClick={toggleRegistration}
                    >
                        {registrationOpen ? 'Tutup Pendaftaran' : 'Buka Pendaftaran'}
                    </Button>
                </div>
            </Card>

            {/* Charts row 1 */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <Card className="lg:col-span-2">
                    <div className="flex items-start justify-between px-5 py-4">
                        <div>
                            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Revenue Overview</h3>
                            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Pendapatan 7 hari terakhir</p>
                        </div>
                        <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                            7 Hari
                        </span>
                    </div>
                    <CardBody className="pt-0">
                        <div className="h-64">
                            <Line data={revenueChartData} options={chartOptions} />
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <div className="px-5 py-4">
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Payment Status</h3>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Distribusi status pembayaran</p>
                    </div>
                    <CardBody className="pt-0">
                        {Object.keys(chartData.statusDistribution || {}).length === 0 ? (
                            <EmptyState title="Belum ada data" description="Distribusi status akan tampil di sini." className="py-10" />
                        ) : (
                            <>
                                <div className="relative h-48">
                                    <Doughnut
                                        data={statusChartData}
                                        options={{ responsive: true, maintainAspectRatio: false, cutout: '68%', plugins: { legend: { display: false } } }}
                                    />
                                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                                        <span className="text-2xl font-bold text-slate-900 dark:text-white">
                                            {Object.values(chartData.statusDistribution || {}).reduce((a, b) => a + b, 0)}
                                        </span>
                                        <span className="text-xs text-slate-400">Total</span>
                                    </div>
                                </div>
                                <div className="mt-4 space-y-2">
                                    {Object.entries(chartData.statusDistribution || {}).map(([status, count], i) => (
                                        <div key={status} className="flex items-center justify-between text-sm">
                                            <span className="flex items-center gap-2 capitalize text-slate-600 dark:text-slate-400">
                                                <span
                                                    className="h-2.5 w-2.5 rounded-full"
                                                    style={{ backgroundColor: statusChartData.datasets[0].backgroundColor[i % 4] }}
                                                />
                                                {status}
                                            </span>
                                            <span className="font-semibold text-slate-800 dark:text-slate-200">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </CardBody>
                </Card>
            </div>

            {/* Charts row 2 */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <Card className="lg:col-span-2">
                    <div className="flex items-start justify-between px-5 py-4">
                        <div>
                            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Payment Analytics</h3>
                            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Jumlah pembayaran per hari</p>
                        </div>
                        <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                            7 Hari
                        </span>
                    </div>
                    <CardBody className="pt-0">
                        <div className="h-64">
                            <Bar data={paymentsChartData} options={chartOptions} />
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <div className="px-5 py-4">
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Recent Payments</h3>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Transaksi terbaru</p>
                    </div>
                    <CardBody className="space-y-3 pt-0">
                        {recentActivity.pembayaran.length === 0 ? (
                            <EmptyState title="Belum ada pembayaran" className="py-8" />
                        ) : (
                            recentActivity.pembayaran.slice(0, 5).map((p, i) => (
                                <div key={i} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 last:border-0 last:pb-0 dark:border-slate-800">
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">{p.kode_toko}</p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            {format(new Date(p.created_at), 'dd MMM yyyy, HH:mm')}
                                        </p>
                                    </div>
                                    <div className="flex flex-shrink-0 items-center gap-2">
                                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                                            Rp {(parseInt(p.amount) || 0).toLocaleString('id-ID')}
                                        </span>
                                        <StatusBadge status={p.keterangan} size="sm" />
                                    </div>
                                </div>
                            ))
                        )}
                    </CardBody>
                </Card>
            </div>

            {/* Activity feed + stores */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <Card className="lg:col-span-2">
                    <div className="px-5 py-4">
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Recent Activity</h3>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Aktivitas terbaru di sistem</p>
                    </div>
                    <CardBody className="pt-0">
                        {feed.length === 0 ? (
                            <EmptyState title="Belum ada aktivitas" className="py-8" />
                        ) : (
                            <ol className="relative space-y-1">
                                {feed.map((item, idx) => {
                                    const style = feedStyles[item.kind];
                                    const Icon = style.icon;
                                    return (
                                        <li key={item.id} className="relative flex gap-3 pb-4 last:pb-0">
                                            {idx !== feed.length - 1 && (
                                                <span className="absolute left-[18px] top-9 h-[calc(100%-2rem)] w-px bg-slate-100 dark:bg-slate-800" />
                                            )}
                                            <span className={`relative z-10 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${style.cls}`}>
                                                <Icon className="h-4 w-4" />
                                            </span>
                                            <div className="min-w-0 flex-1 pt-1">
                                                <div className="flex items-center justify-between gap-3">
                                                    <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">{item.title}</p>
                                                    {item.status && <StatusBadge status={item.status} size="sm" />}
                                                </div>
                                                <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                                                    {item.description} · {timeAgo(item.time)}
                                                </p>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        )}
                    </CardBody>
                </Card>

                <Card>
                    <div className="px-5 py-4">
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Recent Stores</h3>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Toko terbaru terdaftar</p>
                    </div>
                    <CardBody className="space-y-3 pt-0">
                        {recentActivity.toko.length === 0 ? (
                            <EmptyState title="Belum ada toko" className="py-8" />
                        ) : (
                            recentActivity.toko.map((t, i) => (
                                <div key={i} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 last:border-0 last:pb-0 dark:border-slate-800">
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">{t.kode_toko}</p>
                                        <p className="truncate text-xs text-slate-500 dark:text-slate-400">{t.email}</p>
                                    </div>
                                    <span className="flex-shrink-0 text-xs text-slate-400">
                                        {format(new Date(t.created_at), 'dd MMM')}
                                    </span>
                                </div>
                            ))
                        )}
                    </CardBody>
                </Card>
            </div>

            {/* Active paying stores */}
            <Section title="Toko Aktif Pembayaran" subtitle="Toko dengan pembayaran berhasil">
                <Card className="overflow-hidden">
                    <div className="overflow-x-auto scrollbar-slim">
                        <table className="min-w-full border-collapse text-sm">
                            <thead>
                                <tr className="border-b border-slate-200/80 dark:border-slate-800">
                                    {['Kode Toko', 'Email', 'Total Pembayaran', 'Frekuensi', 'Terakhir Bayar', 'Lisensi Terakhir'].map((h) => (
                                        <th key={h} className="whitespace-nowrap px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {recentActivity.activeStores?.map((store, i) => (
                                    <tr key={i} className="transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                                        <td className="whitespace-nowrap px-4 py-3.5 font-semibold text-slate-900 dark:text-slate-100">{store.kode_toko}</td>
                                        <td className="whitespace-nowrap px-4 py-3.5 text-slate-500 dark:text-slate-400">{store.email}</td>
                                        <td className="whitespace-nowrap px-4 py-3.5 text-slate-700 dark:text-slate-300">Rp {store.totalAmount.toLocaleString('id-ID')}</td>
                                        <td className="whitespace-nowrap px-4 py-3.5 text-slate-500 dark:text-slate-400">{store.paymentCount} kali</td>
                                        <td className="whitespace-nowrap px-4 py-3.5 text-slate-500 dark:text-slate-400">{format(new Date(store.latestPayment), 'dd MMM yyyy')}</td>
                                        <td className="whitespace-nowrap px-4 py-3.5 text-slate-500 dark:text-slate-400">{store.expiry_duration ? `${store.expiry_duration} hari` : '-'}</td>
                                    </tr>
                                ))}
                                {(!recentActivity.activeStores || recentActivity.activeStores.length === 0) && (
                                    <tr>
                                        <td colSpan="6">
                                            <EmptyState title="Belum ada toko aktif" description="Toko dengan pembayaran berhasil akan tampil di sini." />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </Section>
        </div>
    );
}
